/**
 * Parses the bulk external-reference upload file.
 *
 * Deliberately forgiving about how the file was produced, because both of these
 * happen in practice: pasting out of a spreadsheet yields tabs rather than commas,
 * and Excel's "CSV UTF-8" prepends a byte-order mark that would otherwise make the
 * first header read as "﻿company_uuid".
 *
 * The two required columns are read positionally. That is safe only because neither
 * a UUID nor a reference value can contain a delimiter — which is why they must stay
 * first. Everything from the third column on is rejoined into `label`, so an unquoted
 * comma inside a firm name survives.
 */

export type RowProblem = "invalid-uuid" | "invalid-value";

export type ParsedRow = {
  /** 1-indexed line in the source file, counting the header, for error reporting. */
  lineNumber: number;
  companyUuid: string;
  referenceValue: string;
  /** Trailing columns joined back together. Display only — never sent to the API. */
  label: string;
  problem: RowProblem | null;
};

export type ParseResult = {
  delimiter: "," | "\t";
  rows: ParsedRow[];
};

export type ParseOptions = {
  /** Chooses the value rule. Defaults to the mondaq_id rule. */
  referenceType?: string;
  /** Overrides the row ceiling. Mainly for tests. */
  maxRows?: number;
};

/**
 * Ceiling on rows accepted from one file. Parsing runs synchronously on the main
 * thread and the results table is not virtualised, so a much larger file would lock
 * the tab up rather than fail cleanly. Comfortably above the ~1k rows this exists for;
 * anything bigger should be split, or the page reworked to stream and virtualise.
 */
export const MAX_ROWS = 5000;

/**
 * Mirrors zod v4's `z.uuid()` on address-book-api: version nibble 1-8 and variant
 * nibble 8/9/a/b. Verified against the real schema. One deliberate divergence — the
 * nil UUID satisfies `z.uuid()` but is rejected here, because it can never match a
 * company and a preview error is more useful than a 404 halfway through a run.
 */
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/** Reference types whose value must be a positive integer. */
const INTEGER_REFERENCE_TYPES = new Set(["mondaq_id"]);

/**
 * Positive integer with no leading zeros. Stricter than the API, which coerces with
 * `Number()` and would accept "15.0" or " 15 ". Values are persisted verbatim, so
 * "015" would validate as 15 and then never match a later "15" — better rejected here.
 */
const POSITIVE_INTEGER_PATTERN = /^[1-9]\d*$/;

const REQUIRED_HEADER = "company_uuid";

function detectDelimiter(header: string): "," | "\t" {
  const tabs = header.split("\t").length - 1;
  const commas = header.split(",").length - 1;
  return tabs > commas ? "\t" : ",";
}

function findProblem(uuid: string, value: string, referenceType: string): RowProblem | null {
  if (!UUID_PATTERN.test(uuid)) {
    return "invalid-uuid";
  }
  if (INTEGER_REFERENCE_TYPES.has(referenceType)) {
    return POSITIVE_INTEGER_PATTERN.test(value) ? null : "invalid-value";
  }
  return value.length > 0 ? null : "invalid-value";
}

export function parseCsv(text: string, options: ParseOptions = {}): ParseResult {
  const referenceType = options.referenceType ?? "mondaq_id";

  const lines = text
    .replace(/^﻿/, "")
    .split(/\r?\n/)
    .map((line, index) => ({ line, lineNumber: index + 1 }))
    .filter(entry => entry.line.trim() !== "");

  const header = lines.shift();
  if (!header || !header.line.toLowerCase().includes(REQUIRED_HEADER)) {
    throw new Error(
      `The file needs a header row containing "${REQUIRED_HEADER}" and a value column. ` +
        "Expected: company_uuid,reference_value",
    );
  }

  const maxRows = options.maxRows ?? MAX_ROWS;
  if (lines.length > maxRows) {
    throw new Error(
      `That file has ${lines.length.toLocaleString()} rows, above the ${maxRows.toLocaleString()} ` +
        "row limit for a single upload. Split it into smaller files and upload them in turn.",
    );
  }

  const delimiter = detectDelimiter(header.line);

  const rows = lines.map(({ line, lineNumber }) => {
    const parts = line.split(delimiter);
    const companyUuid = (parts[0] ?? "").trim();
    const referenceValue = (parts[1] ?? "").trim();
    const label = parts
      .slice(2)
      .join(delimiter)
      .trim()
      .replace(/^"(.*)"$/s, "$1");

    return {
      lineNumber,
      companyUuid,
      referenceValue,
      label,
      problem: findProblem(companyUuid, referenceValue, referenceType),
    };
  });

  return { delimiter, rows };
}
